/**
 * Tax Backend API Service
 * Handles API calls to the tax backend for April integration
 * Uses AWS Amplify REST API for consistency with other API calls
 */
import { get } from "aws-amplify/api";
import type {
  AprilApplicationName,
  AprilHostedUrlResponse,
  UserAppStateResponse,
  UserReturnDataResponse,
  GetUserTaxReturnFileWithDownloadLinkResponse,
  ListUserTaxReturnsResponse,
} from "@/types/tax";

const API_NAME = "TaxAPI";

/**
 * Check if user has signed up with April
 * @param email - User's email address
 * @returns true if user has signed up, false otherwise
 */
export async function checkUserSignedUp(email: string): Promise<boolean> {
  try {
    const response = await get({
      apiName: API_NAME,
      path: "/checkUserSignedUp",
      options: {
        queryParams: { email },
      },
    });
    const result = await response.response;
    const body = await result.body.json();
    return body as unknown as boolean;
  } catch (error) {
    console.error("Failed to check user signup status:", error);
    return false;
  }
}

/**
 * Get April hosted URL for opening in new tab/iframe
 * @param email - User's email address
 * @param applicationName - April application type (FILING, PAYCHECK_ESTIMATION, PAYCHECK_OPTIMIZATION)
 * @param partnerUserId - Optional partner user ID for tracking
 * @returns April hosted URL response
 */
export async function getAprilHostedUrl(
  email: string,
  applicationName: AprilApplicationName,
  partnerUserId?: string,
): Promise<AprilHostedUrlResponse> {
  const queryParams: Record<string, string> = { email, applicationName };
  if (partnerUserId) {
    queryParams.partnerUserId = partnerUserId;
  }

  const response = await get({
    apiName: API_NAME,
    path: "/aprilHostedUrl",
    options: { queryParams },
  });
  const result = await response.response;
  const body = await result.body.json();
  return body as unknown as AprilHostedUrlResponse;
}

/**
 * Get user's application state in April
 * @param email - User's email address
 * @param applicationName - April application type
 * @param taxYear - Tax year (e.g., "2024")
 * @returns User app state response
 */
export async function getUserAppState(
  email: string,
  applicationName: AprilApplicationName,
  taxYear: string,
): Promise<UserAppStateResponse> {
  const response = await get({
    apiName: API_NAME,
    path: "/userAppState",
    options: {
      queryParams: { email, applicationName, taxYear },
    },
  });
  const result = await response.response;
  const body = await result.body.json();
  return body as unknown as UserAppStateResponse;
}

/**
 * Get user's tax return files with download links
 * @param email - User's email address
 * @param applicationName - April application type
 * @param taxYear - Tax year (e.g., "2024")
 * @returns Tax return files with download links
 */
export async function getTaxReturnFiles(
  email: string,
  applicationName: AprilApplicationName,
  taxYear: string,
): Promise<GetUserTaxReturnFileWithDownloadLinkResponse> {
  const response = await get({
    apiName: API_NAME,
    path: "/taxReturnFiles",
    options: {
      queryParams: { email, applicationName, taxYear },
    },
  });
  const result = await response.response;
  const body = await result.body.json();
  return body as unknown as GetUserTaxReturnFileWithDownloadLinkResponse;
}

/**
 * List user's submitted tax returns
 * @param email - User's email address
 * @param applicationName - April application type
 * @param taxYear - Tax year (e.g., "2024")
 * @returns List of user's submitted tax returns
 */
export async function listUserTaxReturns(
  email: string,
  applicationName: AprilApplicationName,
  taxYear: string,
): Promise<ListUserTaxReturnsResponse> {
  const response = await get({
    apiName: API_NAME,
    path: "/listUserTaxReturns",
    options: {
      queryParams: { email, applicationName, taxYear },
    },
  });
  const result = await response.response;
  const body = await result.body.json();
  return body as unknown as ListUserTaxReturnsResponse;
}

/**
 * Get user's return data including refund information
 * @param email - User's email address
 * @param applicationName - April application type
 * @param taxYear - Tax year (e.g., "2024")
 * @returns User return data with refund/owed amount
 */
export async function getUserReturnData(
  email: string,
  applicationName: AprilApplicationName,
  taxYear: string,
): Promise<UserReturnDataResponse> {
  const response = await get({
    apiName: API_NAME,
    path: "/userReturnData",
    options: {
      queryParams: { email, applicationName, taxYear },
    },
  });
  const result = await response.response;
  const body = await result.body.json();
  return body as unknown as UserReturnDataResponse;
}
